import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Firestore, collection, query, where, onSnapshot, Unsubscribe, getDocs } from '@angular/fire/firestore';
import { Observable } from 'rxjs';

export interface PlanLimits {
    maxCongregationSize: number | null;  // null = unlimited
    maxTeamMembers: number | null;
    maxAutomations: number | null;
    maxScheduledMessages: number | null;
    maxTemplates: number | null;
    includesFullAnalytics: boolean;
    /**
     * Whether the plan includes the website chat widget.
     *
     * Mirrors PlanLimits in functions/src/utils/subscription-validator.ts —
     * change both together. Read this flag rather than comparing the plan name
     * or ID: the Paystack plan codes differ between the production and UAT
     * projects, so an ID comparison would work in one and fail in the other.
     *
     * Optional, and absent means not included: a plan document seeded before
     * this field existed must fail closed, not hand out a paid feature.
     */
    includesWebChat?: boolean;
    /** Whether the plan includes access to the public REST API. Enterprise only. */
    includesApiAccess?: boolean;
    /** Whether the plan includes the AI features. Enterprise only. */
    includesAi?: boolean;
    /** Whether the plan includes conversation workflows and list automations. Enterprise only. */
    includesWorkflows?: boolean;
    /** Whether the plan includes SMS integrations (WinSMS / BulkSMS / SMSPortal). */
    includesSms?: boolean;
}

export interface SubscriptionPlan {
    id: string;
    name: string;
    price: number;           // price in cents (ZAR) — used by Paystack
    priceUSD: number;        // approximate USD price in cents — display only
    currency: string;        // always 'ZAR' for Paystack
    interval: string;
    features: string[];
    paystackPlanCode: string;
    recommended?: boolean;
    active?: boolean;
    displayOrder?: number;
    description?: string;
    limits: PlanLimits;
}

@Injectable({
    providedIn: 'root'
})
export class PlansService {
    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);

    /**
     * Get all active subscription plans
     */
    async getActivePlans(): Promise<SubscriptionPlan[]> {
        const plansRef = collection(this.firestore, 'plans');
        const q = query(plansRef, where('active', '==', true));

        const querySnapshot = await runInInjectionContext(this.injector, () => getDocs(q));
        const plans: SubscriptionPlan[] = [];

        querySnapshot.forEach((doc) => {
            plans.push({
                id: doc.id,
                paystackPlanCode: doc.id, // Document ID is the Paystack plan code
                ...doc.data() as Omit<SubscriptionPlan, 'id' | 'paystackPlanCode'>
            });
        });

        // Sort by displayOrder if available
        return plans.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    }

    /**
     * Get real-time updates for active subscription plans
     */
    getActivePlansRealtime(callback: (plans: SubscriptionPlan[]) => void): Unsubscribe {
        const plansRef = collection(this.firestore, 'plans');
        const q = query(plansRef, where('active', '==', true));

        return runInInjectionContext(this.injector, () => onSnapshot(q, (querySnapshot) => {
            const plans: SubscriptionPlan[] = [];

            querySnapshot.forEach((doc) => {
                plans.push({
                    id: doc.id,
                    paystackPlanCode: doc.id, // Document ID is the Paystack plan code
                    ...doc.data() as Omit<SubscriptionPlan, 'id' | 'paystackPlanCode'>
                });
            });

            // Sort by displayOrder if available
            const sortedPlans = plans.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            callback(sortedPlans);
        }));
    }

    /**
     * Get a specific plan by ID
     */
    async getPlanById(planId: string): Promise<SubscriptionPlan | null> {
        const plans = await this.getActivePlans();
        return plans.find(p => p.id === planId) || null;
    }
}
